import React, { useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Eye, EyeOff } from 'lucide-react';
import { useTheme, ThemedButton } from './ThemeContext';

const slotColors = {
  0: {
    base: "rgb(59, 130, 246)",
    glow: "drop-shadow(0 0 6px rgb(59, 130, 246)) drop-shadow(0 0 12px rgb(59, 130, 246))"
  },
  1: {
    base: "rgb(34, 197, 94)",
    glow: "drop-shadow(0 0 6px rgb(34, 197, 94)) drop-shadow(0 0 12px rgb(34, 197, 94))"
  },
  2: {
    base: "rgb(168, 85, 247)",
    glow: "drop-shadow(0 0 6px rgb(168, 85, 247)) drop-shadow(0 0 12px rgb(168, 85, 247))"
  }
};

const pieceToUnicode = (piece, color, isDark) => {
  const symbols = {
    'k': color === 'w' ? (!isDark ? '♔' : '♚') : (isDark ? '♔' : '♚'),
    'q': color === 'w' ? (!isDark ? '♕' : '♛') : (isDark ? '♕' : '♛'),
    'r': color === 'w' ? (!isDark ? '♖' : '♜') : (isDark ? '♖' : '♜'),
    'b': color === 'w' ? (!isDark ? '♗' : '♝') : (isDark ? '♗' : '♝'),
    'n': color === 'w' ? (!isDark ? '♘' : '♞') : (isDark ? '♘' : '♞'),
    'p': color === 'w' ? (!isDark ? '♙' : '♟') : (isDark ? '♙' : '♟')
  };
  return symbols[piece.toLowerCase()] || '';
};

const formatMoveNotation = (move, isDark) => {
  if (!move || move.san === '❓') return '❓';

  let san = move.san;
  const isCheck = san.endsWith('+');
  const isCheckmate = san.endsWith('#');

  if (san.includes('=')) {
    let cleanSan = san;
    if (isCheck) cleanSan = san.slice(0, -1);
    if (isCheckmate) cleanSan = san.slice(0, -1);

    const promotedPiece = cleanSan.split('=')[1].toLowerCase();
    const pieceSymbol = pieceToUnicode(promotedPiece, move.color, isDark);

    return san.replace(/=[QRBN]/i, `=${pieceSymbol}`);
  }

  if (move?.piece === 'p' || san.includes('-')) {
    return pieceToUnicode(move.piece, move.color, isDark) + san;
  }
  return pieceToUnicode(move.piece, move.color, isDark) + san.slice(1);
};

const MoveHistory = ({ moves, onPositionChange, currentPosition, showUnselected = true, onToggleUnselected }) => {
  const { isDark, woodStyles } = useTheme();

  const formatMove = (move) => {
    if (!move) return '❓';

    // For completed slot chess games, format with additional moves
    if (move.slotMoves?.length > 1) {
      if (!showUnselected) {
        return formatMoveNotation(move, isDark);
      }

      const slots = Array(3).fill(null).map((_, i) => {
        // Find move in this slot position if any
        const slotMove = move.slotMoves.find((m, idx) => idx === i);

        if (!slotMove) return <span key={i}>❓</span>;

        const moveText = formatMoveNotation(slotMove, isDark);

        if (slotMove.san === move.san) {
          // Played move - regular formatting
          return <span key={i}>{moveText}</span>;
        } else {
          // Unplayed moves - use slot colors with reduced opacity
          return (
            <span
              key={i}
              style={{
                color: slotColors[i].base,
                opacity: 0.6
              }}
            >
              {moveText}
            </span>
          );
        }
      });

      // Join slots with separator
      return slots.reduce((acc, elem, i) => {
        if (i === 0) return [elem];
        return [...acc, ' • ', elem];
      }, []);
    }

    // Regular move formatting
    return formatMoveNotation(move, isDark);
  };

  const handleMoveClick = useCallback((index) => {
    onPositionChange(index);
  }, [onPositionChange]);

  const handleNavigationClick = useCallback((index) => {
    if (index < 0) return;
    onPositionChange(index);
  }, [onPositionChange]);


  // Group moves into pairs for display
  const moveRows = [];
  for (let i = 0; i < moves.length; i += 2) {
    moveRows.push({
      number: Math.floor(i / 2) + 1,
      white: moves[i],
      black: moves[i + 1]
    });
  }
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        handleNavigationClick(Math.max(0, currentPosition - 1));
      } else if (e.key === 'ArrowRight') {
        handleNavigationClick(Math.min(moves.length - 1, currentPosition + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [moves.length, currentPosition, handleNavigationClick]);

  return (
    <div className="flex flex-col h-full">
      {/* Navigation controls */}
      <div className="flex justify-center gap-2 mb-2 items-center">
        <ThemedButton
          onClick={() => handleNavigationClick(0)}
          disabled={currentPosition <= 0}
          variant="accent"
          className="p-2 w-10 h-5 flex items-center justify-center"
        >
          <ChevronsLeft className={`w-5 h-5 ${woodStyles.text.primary}`} strokeWidth={3.5} />
        </ThemedButton>
        <ThemedButton
          onClick={() => handleNavigationClick(Math.max(0, currentPosition - 1))}
          disabled={currentPosition <= 0}
          variant="accent"
          className="p-2 w-10 h-5 flex items-center justify-center"
        >
          <ChevronLeft className={`w-5 h-5 ${woodStyles.text.primary}`} strokeWidth={3.5} />
        </ThemedButton>
        <ThemedButton
          onClick={() => handleNavigationClick(Math.min(moves.length - 1, currentPosition + 1))}
          disabled={currentPosition === moves.length - 1}
          variant="accent"
          className="p-2 w-10 h-5 flex items-center justify-center"
        >
          <ChevronRight className={`w-5 h-5 ${woodStyles.text.primary}`} strokeWidth={3.5} />
        </ThemedButton>
        <ThemedButton
          onClick={() => handleNavigationClick(moves.length - 1)}
          disabled={currentPosition === moves.length - 1}
          variant="accent"
          className="p-2 w-10 h-5 flex items-center justify-center"
        >
          <ChevronsRight className={`w-5 h-5 ${woodStyles.text.primary}`} strokeWidth={3.5} />
        </ThemedButton>
        <ThemedButton
          onClick={onToggleUnselected}
          variant="accent"
          className="p-2 h-5 flex items-center justify-center"
        >
          {showUnselected ? (
            <Eye className={`w-4 h-4 ${woodStyles.text.primary}`} strokeWidth={2.5} />
          ) : (
            <EyeOff className={`w-4 h-4 ${woodStyles.text.primary}`} strokeWidth={2.5} />
          )}
        </ThemedButton>
      </div>


      {/* Move list */}
      <div className="h-[calc(100%-3rem)] overflow-y-auto">
        <table className="w-full table-fixed">
          <tbody>
            {moveRows.map(({ number, white, black }) => (
              <tr
                key={number}
                className={`border-b border-opacity-10 last:border-0`}
                style={{ borderColor: woodStyles.borderColor }}
              >
                <td className={`w-8 px-2 py-1 text-center ${woodStyles.text.secondary}`}>
                  {number}.
                </td>
                <td
                  onClick={() => white && handleMoveClick((number - 1) * 2)}
                  className={`px-2 py-1 cursor-pointer whitespace-normal break-words ${currentPosition === (number - 1) * 2
                      ? woodStyles.surface.primary
                      : 'hover:bg-black/50'
                    } ${woodStyles.text.primary} transition-colors font-mono`}
                >
                  {white && formatMove(white)}
                </td>
                <td
                  onClick={() => black && handleMoveClick((number - 1) * 2 + 1)}
                  className={`px-2 py-1 cursor-pointer whitespace-normal break-words ${currentPosition === (number - 1) * 2 + 1
                      ? woodStyles.surface.primary
                      : 'hover:bg-black/50'
                    } ${woodStyles.text.primary} transition-colors font-mono`}
                >
                  {black && formatMove(black)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {moveRows.length === 0 && (
          <div className={`text-center p-4 ${woodStyles.text.secondary}`}>
            No moves yet
          </div>
        )}
      </div>
    </div>
  );
};

export default MoveHistory;